import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Dices, PencilLine, Sparkles, BookOpen, Route, Minimize2 } from "lucide-react";
import { api, resolveUrl } from "@/lib/api";
import SegmentSlider from "@/components/SegmentSlider";
import CurioStatusPlate from "@/components/CurioStatusPlate";
import TattooRequestModal from "@/components/TattooRequestModal";
import TattooPhotoLightbox from "@/components/TattooPhotoLightbox";
import WorkflowModal from "@/components/WorkflowModal";
import SmallTattooModal from "@/components/SmallTattooModal";
import MatsiLikeCard from "@/components/MatsiLikeCard";
import "@/components/DystopiaMixer.css";

const preload = (url) => {
  if (!url) return;
  const img = new Image();
  img.src = resolveUrl(url);
};

const isCompositionSet = (set) => {
  const name = (set?.name || "").toLowerCase();
  return name.includes("oldschool") || name.includes("old school") || name.includes("mix & match");
};

const isMatsiSet = (set) => {
  const name = (set?.name || "").trim().toLowerCase();
  return ["matsi", "matze", "matsi set", "matze set"].includes(name);
};

const MATSI_STORY = "Diese Seite hat meine Tochter entworfen. TattooGen ist ursprünglich beim gemeinsamen Malen mit meinen Kindern entstanden. Wir wollten immer wieder neue Figuren haben, die wir zusammen zeichnen können. Aus diesem Spiel ist nach und nach die Idee für TattooGen entstanden. Wenn dir eine Kombination besonders gut gefällt, gib ihr ein Herz. So sieht Matsi, welche ihrer Ideen euch am besten gefallen.";

// Runtime asset lives in frontend/public on the VPS. Keep the frame separate
// from the generated tattoo slices so swapping the visual shell never changes
// mixer data or import logic.
const FRAME_SRC = "/dystopia-frame-web.webp";
const BACKGROUND_SRC = "/dystopia-background-web.webp";

export default function TattooMixer() {
  const [sets, setSets] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [idx, setIdx] = useState({ TOP: 0, MIDDLE: 0, BOTTOM: 0 });
  const [dir, setDir] = useState({ TOP: 1, MIDDLE: 1, BOTTOM: 1 });
  const [combo, setCombo] = useState(null);
  const [comboLoading, setComboLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [requestMode, setRequestMode] = useState("standard");
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [smallTattooOpen, setSmallTattooOpen] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const debounceRef = useRef(null);
  const workflowTriggerRef = useRef(null);
  const smallTattooTriggerRef = useRef(null);

  const loadSet = useCallback((setId) => {
    setErr(null);
    setCombo(null);
    setIdx({ TOP: 0, MIDDLE: 0, BOTTOM: 0 });
    setDir({ TOP: 1, MIDDLE: 1, BOTTOM: 1 });
    api
      .get("/public/active-set", { params: setId ? { set_id: setId } : {} })
      .then((r) => setData(r.data))
      .catch(() => setErr("Dieses Set ist gerade nicht verfügbar."));
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/public/sets").catch(() => ({ data: [] })),
      api.get("/public/active-set"),
    ])
      .then(([setResult, activeResult]) => {
        setSets(setResult.data || []);
        setData(activeResult.data);
      })
      .catch(() => setErr("Kein aktives Set gefunden."));
  }, []);

  const segments = data?.segments;
  const setId = data?.set?.id;
  const compositionMode = isCompositionSet(data?.set);
  const matsiMode = isMatsiSet(data?.set);

  const selectedSlots = useMemo(() => {
    if (!segments) return null;
    return {
      top: segments.TOP[idx.TOP]?.slot,
      middle: segments.MIDDLE[idx.MIDDLE]?.slot,
      bottom: segments.BOTTOM[idx.BOTTOM]?.slot,
    };
  }, [segments, idx]);

  const change = useCallback((type, delta) => {
    if (!segments) return;
    const len = segments[type].length;
    if (!len) return;
    setDir((d) => ({ ...d, [type]: delta }));
    setIdx((s) => ({ ...s, [type]: (s[type] + delta + len) % len }));
  }, [segments]);

  const shuffle = useCallback(() => {
    if (!segments) return;
    const r = (t) => Math.floor(Math.random() * segments[t].length);
    setDir({ TOP: 1, MIDDLE: 1, BOTTOM: 1 });
    setIdx({ TOP: r("TOP"), MIDDLE: r("MIDDLE"), BOTTOM: r("BOTTOM") });
  }, [segments]);

  useEffect(() => {
    const onKey = (e) => {
      if (modalOpen || workflowOpen || smallTattooOpen || lightbox) return;
      if (e.key === "ArrowLeft") change("MIDDLE", -1);
      else if (e.key === "ArrowRight") change("MIDDLE", 1);
      else if (e.key === " ") {
        e.preventDefault();
        shuffle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [change, shuffle, modalOpen, workflowOpen, smallTattooOpen, lightbox]);

  useEffect(() => {
    if (!segments) return;
    ["TOP", "MIDDLE", "BOTTOM"].forEach((t) => {
      const arr = segments[t];
      const i = idx[t];
      preload(arr[(i + 1) % arr.length]?.asset_url);
      preload(arr[(i - 1 + arr.length) % arr.length]?.asset_url);
    });
  }, [segments, idx]);

  useEffect(() => {
    if (!setId || !segments || !selectedSlots?.top || !selectedSlots?.middle || !selectedSlots?.bottom) return;
    setComboLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api
        .get("/public/combination", {
          params: {
            set_id: setId,
            top: selectedSlots.top,
            middle: selectedSlots.middle,
            bottom: selectedSlots.bottom,
          },
        })
        .then((r) => setCombo(r.data))
        .catch(() => setCombo(null))
        .finally(() => setComboLoading(false));
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [setId, segments, selectedSlots]);

  const refreshCombo = () => {
    if (!setId || !selectedSlots) return;
    api
      .get("/public/combination", {
        params: {
          set_id: setId,
          top: selectedSlots.top,
          middle: selectedSlots.middle,
          bottom: selectedSlots.bottom,
        },
      })
      .then((r) => setCombo(r.data));
  };

  const openRequest = (mode = "standard") => {
    setRequestMode(mode);
    setModalOpen(true);
  };

  if (err) {
    return (
      <div className="single-page-shell single-page-center">
        <div className="single-error-card">
          <div className="brand-wordmark">ARTPART</div>
          <p>{err}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="single-page-shell single-page-center">
        <div className="single-loading">TattooGen lädt…</div>
      </div>
    );
  }

  const status = combo?.status || "FREE";
  const isFree = status === "FREE";
  const isFinished = status === "TATTOOED";
  const topLabel = compositionMode ? "1. Hauptmotiv" : "1. Kopf";
  const middleLabel = compositionMode ? "2. Blume" : "2. Körper";
  const bottomLabel = compositionMode ? "3. Element" : "3. Beine";
  const setDescription = matsiMode ? MATSI_STORY : data.set.description?.trim();
  const publicSetName = matsiMode ? "Matsi" : data.set.name;

  return (
    <main
      className="single-page-shell"
      data-set-theme="dystopia"
      style={{ "--dystopia-background": `url(${BACKGROUND_SRC})` }}
    >
      <div className="single-page-inner">
        <header className="single-topbar">
          <a href="/" className="brand-home-link" aria-label="TattooGen neu laden">
            <div className="brand-wordmark">ARTPART</div>
            <div className="brand-subline">TattooGen</div>
          </a>
          <a href="https://www.artpart.at/" className="artist-mark artist-link" aria-label="Zur ARTPART Website">
            <strong>René Ghavami</strong>
            <span>Tätowierer · ARTPART</span>
          </a>
        </header>

        {sets.length ? (
          <nav className="set-strip" aria-label="Tattoo-Sets">
            {sets.map((set) => {
              const active = set.id === setId;
              return (
                <button
                  key={set.id}
                  type="button"
                  className={`set-chip ${active ? "is-active" : ""}`}
                  onClick={() => !active && loadSet(set.id)}
                >
                  <span className="set-chip-symbol">✦</span>
                  <span>{isMatsiSet(set) ? "Matsi" : set.name}</span>
                </button>
              );
            })}
          </nav>
        ) : null}

        <section className="single-main-grid">
          <aside className="single-intro">
            <div className="eyebrow">TattooGen</div>
            <h1>Deine<br /><span className="headline-accent">Wahl.</span></h1>
            <div className="pink-rule" />
            <div className="eyebrow section-gap">Dein Set</div>
            <h2>{publicSetName}</h2>
            <p className="intro-copy">
              {setDescription || "Misch dir eine Kombination zusammen, die dich packt. Deine Auswahl ist der Ausgangspunkt. Daraus entwickle ich anschließend eine eigenständige, tattoo-taugliche Figur."}
            </p>
            <div className="intro-points">
              <span>{compositionMode ? "Drei Motive werden zu einer neuen Komposition" : "Jede Auswahl führt zu einer eigenen Idee"}</span>
              <span>Bis zu 1.000 Kombinationen pro Set</span>
              <span>Für Körperstelle, Größe und Stil neu ausgearbeitet</span>
            </div>
            <div className="mini-story-card">
              <BookOpen size={20} />
              <div>
                <strong>Du wählst. Ich entwickle weiter.</strong>
                <span>Die Mischung wird nicht kopiert. Sie gibt die Richtung vor. Die eigentliche Gestaltung entsteht danach mit den Arbeitsschritten, die dem Motiv dienen.</span>
              </div>
            </div>
          </aside>

          <div className="mixer-column">
            <div className="alpine-frame-shell theme-frame theme-frame-dystopia" data-testid="tattoo-mixer-frame">
              <div className="alpine-frame-window">
                <SegmentSlider
                  items={segments.TOP} index={idx.TOP} direction={dir.TOP}
                  onChange={(d) => change("TOP", d)} label={topLabel}
                  testid="segment-slider-top" prevTestid="btn-prev-top" nextTestid="btn-next-top"
                />
                <SegmentSlider
                  items={segments.MIDDLE} index={idx.MIDDLE} direction={dir.MIDDLE}
                  onChange={(d) => change("MIDDLE", d)} label={middleLabel}
                  testid="segment-slider-middle" prevTestid="btn-prev-middle" nextTestid="btn-next-middle"
                />
                <SegmentSlider
                  items={segments.BOTTOM} index={idx.BOTTOM} direction={dir.BOTTOM}
                  onChange={(d) => change("BOTTOM", d)} label={bottomLabel}
                  testid="segment-slider-bottom" prevTestid="btn-prev-bottom" nextTestid="btn-next-bottom"
                />
              </div>
              <img
                src={FRAME_SRC}
                alt=""
                className="alpine-frame-art"
                aria-hidden="true"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>

            {matsiMode ? <MatsiLikeCard setId={setId} selectedSlots={selectedSlots} /> : null}

            <div className="action-deck">
              <CurioStatusPlate
                number={combo?.number}
                status={status}
                priceLabel={data.set.price_label}
                hasPhoto={!!combo?.completed_image_url}
                onViewPhoto={() => setLightbox(true)}
                loading={comboLoading}
              />
              <div className="action-row">
                <button type="button" onClick={shuffle} data-testid="btn-shuffle-mixer" className="shuffle-cta">
                  <Dices size={19} /> Neu würfeln
                </button>
                {isFree ? (
                  <button type="button" onClick={() => openRequest("standard")} data-testid="btn-request-tattoo-cta" className="request-cta">
                    Meine Wahl anfragen <span>→</span>
                  </button>
                ) : (
                  <div className="request-cta disabled-cta">
                    {status === "RESERVED" ? "Reserviert" : "Vergeben"}
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="single-explainer">
            <div className="eyebrow">So entsteht dein Tattoo</div>
            <h3 className="workflow-copy-title">Aus deiner Auswahl entsteht eine neue Figur.</h3>
            <p className="explainer-lead">
              Deine Kombination ist der Ausgangspunkt. Sie ist nicht das fertige Tattoo. Ich verbinde die ausgewählten Elemente, verändere Übergänge, Haltung, Proportionen und Details und entwickle daraus eine eigenständige, tattoo-taugliche Komposition. Dabei kombiniere ich je nach Motiv Skizzen, Handarbeit, Procreate und digitale oder KI-gestützte Werkzeuge. Entscheidend ist, was dem Motiv dient und auf der gewählten Körperstelle funktioniert.
            </p>
            <p className="workflow-copy-note">Keine starre Übernahme. Kein automatisches Endergebnis. Jede Auswahl wird neu interpretiert und für dein Tattoo vorbereitet.</p>

            <div className="process-row">
              <div><span className="process-icon"><Dices size={20} /></span><small>Auswahl</small></div>
              <b>→</b>
              <div><span className="process-icon"><PencilLine size={20} /></span><small>Ausarbeitung</small></div>
              <b>→</b>
              <div><span className="process-icon"><Sparkles size={20} /></span><small>Tattoo</small></div>
            </div>

            <button
              ref={workflowTriggerRef}
              type="button"
              className="workflow-trigger"
              onClick={() => setWorkflowOpen(true)}
              aria-haspopup="dialog"
            >
              <Route size={18} /> So entsteht dein Motiv
            </button>

            {isFree ? (
              <button
                ref={smallTattooTriggerRef}
                type="button"
                className="workflow-trigger small-tattoo-trigger"
                onClick={() => setSmallTattooOpen(true)}
                data-testid="btn-request-small-tattoo"
                aria-haspopup="dialog"
              >
                <Minimize2 size={18} />
                <span>Kleine Variante anfragen</span>
                <small className="small-tattoo-meta">150–300 € · bewusst reduziert</small>
              </button>
            ) : null}

            <div className="status-section">
              <div className="eyebrow">Status dieser Auswahl</div>
              <div className="status-pills">
                <span className={status === "FREE" ? "status-pill active free" : "status-pill free"}>Frei</span>
                <span className={status === "RESERVED" ? "status-pill active reserved" : "status-pill reserved"}>Reserviert</span>
                <span className={status === "TATTOOED" || status === "DISABLED" ? "status-pill active taken" : "status-pill taken"}>Vergeben</span>
              </div>
            </div>

            <div className="result-circles result-circles-single">
              <button
                type="button"
                className={`result-item ${isFinished && combo?.completed_image_url ? "is-ready" : ""}`}
                onClick={() => isFinished && combo?.completed_image_url && setLightbox(true)}
                disabled={!isFinished || !combo?.completed_image_url}
              >
                <span className="result-circle"><Sparkles size={24} /></span>
                <strong>Tattoo</strong>
                <small>{isFinished ? "fertig" : "noch nicht erstellt"}</small>
              </button>
            </div>
          </aside>
        </section>

        <footer className="studio-partner-cta">
          <div>
            <span>Für Tattoo-Studios</span>
            <strong>Du möchtest TattooGen in deinem eigenen Stil?</strong>
          </div>
          <a href="/studio">TattooGen für dein Studio <span>→</span></a>
        </footer>
        <nav className="tattoogen-legal-links" aria-label="Rechtliche Informationen">
          <a href="https://neu.artpart.at/datenschutz">Datenschutz</a>
          <a href="https://neu.artpart.at/impressum">Impressum</a>
        </nav>
      </div>

      <WorkflowModal
        open={workflowOpen}
        onClose={() => setWorkflowOpen(false)}
        onRequest={() => isFree ? openRequest("standard") : window.scrollTo({ top: 0, behavior: "smooth" })}
        triggerRef={workflowTriggerRef}
        hasActiveCombination={!!selectedSlots?.top && !!selectedSlots?.middle && !!selectedSlots?.bottom && isFree}
      />
      <SmallTattooModal
        open={smallTattooOpen}
        onClose={() => setSmallTattooOpen(false)}
        onRequest={() => openRequest("small")}
        triggerRef={smallTattooTriggerRef}
      />
      <TattooRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        setData={data}
        idx={idx}
        selectedSlots={selectedSlots}
        combo={combo}
        onSuccess={refreshCombo}
        requestMode={requestMode}
      />
      <TattooPhotoLightbox
        open={lightbox}
        onClose={() => setLightbox(false)}
        imageUrl={combo?.completed_image_url}
        title={combo?.completed_title}
        description={combo?.completed_description}
      />
    </main>
  );
}